import { useCallback, useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import { useIntlAccaTournaments } from "./useIntlAccaTournaments.ts";
import type { IntlAccaTournament } from "../db/intl-acca-types.ts";

export interface BetBuilderLeg {
  id: string;
  market_code: string;
  market_label: string | null;
  family: string | null;
  selection: string | null;
  team_side: string | null;
  player_name: string | null;
  line_value: number | null;
  fair_prob: number | null;
  fair_odds: number | null;
  result: "win" | "loss" | "void" | "pending" | null;
}

export interface BetBuilderSlip {
  id: string;
  match_id: string;
  fixture_label: string | null;
  kickoff: string | null;
  slip_kind: "match_props" | "full_builder";
  leg_count: number;
  combined_fair_prob: number | null;
  combined_odds: number | null;
  status: "ACTIVE" | "WON" | "LOST" | "VOID" | "EXPIRED";
  result_note: string | null;
  composed_by: "swarm" | "engine";
  swarm_note: string | null;
  legs: BetBuilderLeg[];
  home_code: string | null;
  away_code: string | null;
}

export interface IntlBetBuilderVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slips: BetBuilderSlip[];
  refetch: () => Promise<void>;
}

export function useIntlBetBuilder(): IntlBetBuilderVM {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const { tournaments, activeId } = useIntlAccaTournaments();
  const tournamentId = activeId ?? tournaments[0]?.id ?? null;
  const [s, setS] = useState<Omit<IntlBetBuilderVM, "refetch">>({
    loading: true, error: null, tournament: null, slips: [],
  });

  const fetchAll = useCallback(async () => {
    if (!tournamentId) {
      setS({ loading: false, error: null, tournament: null, slips: [] });
      return;
    }
    setS((p) => ({ ...p, loading: true, error: null }));
    try {
      const [tRes, sRes] = await Promise.all([
        supabase.from("intl_acca_tournaments").select("*").eq("id", tournamentId).maybeSingle(),
        // GENUINE AGENT SIGNALS ONLY: the dedicated Bet Builder page shows only
        // swarm-composed slips. Engine-floor slips (composed_by='engine') ship
        // for continuity + the Super Acca BYO panel, but are never presented here
        // as if the agents built them.
        supabase.from("intl_bet_builder_slips").select("*")
          .eq("tournament_id", tournamentId).eq("status", "ACTIVE")
          .eq("composed_by", "swarm")
          .order("kickoff", { ascending: true }),
      ]);
      if (tRes.error) throw tRes.error;
      const slips = (sRes.data ?? []) as BetBuilderSlip[];

      const slipIds = slips.map((x) => x.id);
      const lgRes = slipIds.length
        ? await supabase.from("intl_bet_builder_legs").select("*").in("slip_id", slipIds)
        : { data: [], error: null };
      const legs = (lgRes.data ?? []) as Array<BetBuilderLeg & { slip_id: string }>;
      const bySlip = new Map<string, BetBuilderLeg[]>();
      for (const l of legs) {
        if (!bySlip.has(l.slip_id)) bySlip.set(l.slip_id, []);
        bySlip.get(l.slip_id)!.push(l);
      }

      // Resolve home/away nation codes for the flag strip.
      const matchIds = [...new Set(slips.map((x) => x.match_id))];
      const codes: Record<string, { home: string | null; away: string | null }> = {};
      if (matchIds.length) {
        const mRes = await supabase.from("intl_acca_matches")
          .select("match_id,home,away").in("match_id", matchIds);
        for (const m of (mRes.data ?? []) as Array<{ match_id: string; home: string | null; away: string | null }>) {
          codes[m.match_id] = { home: m.home, away: m.away };
        }
      }

      const withLegs: BetBuilderSlip[] = slips.map((x) => ({
        ...x,
        legs: (bySlip.get(x.id) ?? []).sort((a, b) => (b.fair_prob ?? 0) - (a.fair_prob ?? 0)),
        home_code: codes[x.match_id]?.home ?? null,
        away_code: codes[x.match_id]?.away ?? null,
      }));

      setS({
        loading: false, error: null,
        tournament: (tRes.data as IntlAccaTournament | null) ?? null,
        slips: withLegs,
      });
    } catch (e) {
      setS({ loading: false, error: e instanceof Error ? e.message : String(e), tournament: null, slips: [] });
    }
  }, [supabase, tournamentId]);

  useEffect(() => { void fetchAll(); }, [fetchAll]);
  return { ...s, refetch: fetchAll };
}
